import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';
import { riddledInDebug } from './debug/riddledInDebug';
import {
  normalizeRiddledInStart,
  normalizeRiddledInResume,
  normalizeRiddledInGuess,
  normalizeRiddledInReveal,
} from './riddledInResponse';

const nowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const RIDDLED_IN_REQUEST_TIMEOUT_MS = 45_000;

const fetchWithTimeout = async (
  url,
  options,
  { timeoutMs = RIDDLED_IN_REQUEST_TIMEOUT_MS, label = 'fetch' } = {},
) => {
  const startedAt = nowMs();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    riddledInDebug(`${label}:response`, {
      status: res?.status,
      ok: Boolean(res?.ok),
      durationMs: Math.round(nowMs() - startedAt),
    });
    return res;
  } catch (err) {
    const durationMs = Math.round(nowMs() - startedAt);
    const isAbort = err?.name === 'AbortError';
    riddledInDebug(`${label}:error`, {
      name: err?.name || null,
      message: err?.message || String(err),
      durationMs,
      aborted: isAbort,
    });
    if (isAbort) {
      const e = new Error(`Request timed out after ${timeoutMs}ms`);
      e.code = 'TIMEOUT';
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/riddled-in`;
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let dataJson = null;
  try {
    dataJson = text ? JSON.parse(text) : null;
  } catch {
    dataJson = null;
  }
  return { text, dataJson };
};

const buildAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt
    ? jwtAnonRaw
    : anonLooksJwt
      ? anonRaw
      : null;

  const accessToken = await getValidAccessToken();
  riddledInDebug('auth:token', {
    hasAccessToken: Boolean(accessToken),
    hasApiKey: Boolean(apikeyToSend),
  });

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }

  return headers;
};

const requestDemoAction = async ({ action, gameId, guess, label }) => {
  const headers = await buildAuthHeaders();
  const response = await fetchWithTimeout(
    getFunctionsUrl(),
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action,
        ...(gameId ? { gameId } : {}),
        ...(typeof guess === 'string' ? { guess } : {}),
      }),
    },
    { label, timeoutMs: RIDDLED_IN_REQUEST_TIMEOUT_MS },
  );

  const { text, dataJson } = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(dataJson?.error || text || 'Riddled-In demo request failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  return { text, dataJson };
};

/**
 * Start a new Riddled-In game.
 * The edge function picks a random word, generates a riddle via AI Gateway,
 * and charges 20 coins.
 *
 * @returns {{ gameId: string, riddle: string, wordLength: number, cost: number, coins: number }}
 */
export const startRiddledInGame = async () => {
  riddledInDebug('api:start:begin');
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const doRequest = async (reqHeaders) =>
    await fetchWithTimeout(
      getFunctionsUrl(),
      {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({ action: 'start' }),
      },
      { label: 'api:start', timeoutMs: RIDDLED_IN_REQUEST_TIMEOUT_MS },
    );

  let response = await doRequest(headers);

  // If the access token expired, refresh once and retry.
  if (response.status === 401) {
    riddledInDebug('api:start:retry_401');
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const { text, dataJson } = await parseJsonResponse(response);

  if (!response.ok) {
    riddledInDebug('api:start:failed', {
      status: response.status,
      code: dataJson?.code || null,
      message: dataJson?.error || text || null,
    });
    const error = new Error(
      dataJson?.error || text || 'Failed to start game.',
    );
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  const normalized = normalizeRiddledInStart(dataJson || {});

  if (
    !normalized.riddle ||
    !normalized.gameId ||
    typeof normalized.wordLength !== 'number' ||
    !Number.isFinite(normalized.wordLength) ||
    normalized.wordLength <= 0
  ) {
    // Include a tiny snippet of the raw body for debugging without spamming logs.
    const snippet = String(text || '').slice(0, 220);

    // Special case: this strongly suggests the deployed edge function is an older version
    // that returns `{ riddle, word, cost, coins }` but does not create a DB game session.
    // Without a `gameId`, the user cannot submit guesses/reveal/resume properly.
    if (!normalized.gameId && (dataJson?.word || dataJson?.secret_word) && dataJson?.riddle) {
      throw new Error(
        'Riddled-In edge function is out of date: start response is missing gameId/wordLength. ' +
          'Deploy the latest `riddled-in` function, then try again. ' +
          `Received: ${snippet || '(empty response)'}`,
      );
    }
    throw new Error(
      `Invalid game response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Resume the most recent Riddled-In game for the current user.
 *
 * @returns {{ gameId: string, riddle: string, wordLength: number, status: string, word?: string }}
 */
export const resumeRiddledInGame = async () => {
  riddledInDebug('api:resume:begin');
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const doRequest = async (reqHeaders) =>
    await fetchWithTimeout(
      getFunctionsUrl(),
      {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({ action: 'resume' }),
      },
      { label: 'api:resume', timeoutMs: RIDDLED_IN_REQUEST_TIMEOUT_MS },
    );

  let response = await doRequest(headers);

  // Same resiliency as start: refresh-once on 401.
  if (response.status === 401) {
    riddledInDebug('api:resume:retry_401');
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const { text, dataJson } = await parseJsonResponse(response);

  if (!response.ok) {
    riddledInDebug('api:resume:failed', {
      status: response.status,
      code: dataJson?.code || null,
      message: dataJson?.error || text || null,
    });
    const error = new Error(dataJson?.error || text || 'Resume failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  const normalized = normalizeRiddledInResume(dataJson || {});

  if (
    !normalized.gameId ||
    !normalized.riddle ||
    typeof normalized.wordLength !== 'number' ||
    !Number.isFinite(normalized.wordLength) ||
    normalized.wordLength <= 0
  ) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid resume response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Submit a guess for a running game.
 *
 * @returns {{ percentage: number, isCorrect: boolean, lettersInWord?: boolean[], status: string, wordLength: number }}
 */
export const submitRiddledInGuess = async ({ gameId, guess }) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'guess', gameId, guess }),
  });

  const { text, dataJson } = await parseJsonResponse(response);

  if (!response.ok) {
    const error = new Error(dataJson?.error || text || 'Guess failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  const normalized = normalizeRiddledInGuess(dataJson || {});
  if (
    typeof normalized.percentage !== 'number' ||
    !Number.isFinite(normalized.percentage)
  ) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid guess response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Reveal the secret word (give up).
 *
 * @returns {{ word: string, status: string, wordLength: number }}
 */
export const revealRiddledInWord = async ({ gameId }) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'reveal', gameId }),
  });

  const { text, dataJson } = await parseJsonResponse(response);

  if (!response.ok) {
    const error = new Error(dataJson?.error || text || 'Reveal failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  const normalized = normalizeRiddledInReveal(dataJson || {});
  if (!normalized.word) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid reveal response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Start a guest demo game with a pre-generated riddle.
 *
 * @returns {{ gameId: string, riddle: string, wordLength: number }}
 */
export const startRiddledInDemoGame = async () => {
  const { text, dataJson } = await requestDemoAction({
    action: 'demo_start',
    label: 'api:demo_start',
  });

  const normalized = normalizeRiddledInStart(dataJson || {});
  if (
    !normalized.riddle ||
    !normalized.gameId ||
    typeof normalized.wordLength !== 'number' ||
    !Number.isFinite(normalized.wordLength) ||
    normalized.wordLength <= 0
  ) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid demo game response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Submit a guess for a guest demo game.
 *
 * @returns {{ percentage: number, isCorrect: boolean, lettersInWord?: boolean[], status: string, wordLength: number }}
 */
export const submitRiddledInDemoGuess = async ({ gameId, guess }) => {
  const { text, dataJson } = await requestDemoAction({
    action: 'demo_guess',
    gameId,
    guess,
    label: 'api:demo_guess',
  });

  const normalized = normalizeRiddledInGuess(dataJson || {});
  if (
    typeof normalized.percentage !== 'number' ||
    !Number.isFinite(normalized.percentage)
  ) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid demo guess response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};

/**
 * Reveal the word for a guest demo game.
 *
 * @returns {{ word: string, status: string, wordLength: number }}
 */
export const revealRiddledInDemoWord = async ({ gameId }) => {
  const { text, dataJson } = await requestDemoAction({
    action: 'demo_reveal',
    gameId,
    label: 'api:demo_reveal',
  });

  const normalized = normalizeRiddledInReveal(dataJson || {});
  if (!normalized.word) {
    const snippet = String(text || '').slice(0, 220);
    throw new Error(
      `Invalid demo reveal response. Received: ${snippet || '(empty response)'}`,
    );
  }

  return normalized;
};
